import type { HelpLanguage } from "./helpLanguage";
import { HELP_PAGE_IDS, type HelpPageId } from "./helpPages";

/**
 * The help's text: one Markdown file per page and language, `<language>/<page>.md`, whose
 * first line `# …` is its title. Every `##` and `###` heading names its section id as
 * `{#id}`, the same in each language, so a link to a section works in either. The
 * help center and the graph legend read this content; explanations are authored here once.
 */
export interface HelpSection {
  readonly id: string;
  readonly heading: string;
  readonly depth: 2 | 3;
  /** The section's own text, up to the next heading, as Markdown. */
  readonly body: string;
}

export interface HelpPage {
  readonly id: HelpPageId;
  readonly title: string;
  /** The text before the first section. */
  readonly lead: string;
  readonly sections: readonly HelpSection[];
  readonly markdown: string;
}

const TITLE = /^# (.+)$/;
const HEADING = /^(#{2,3}) (.+?) \{#([a-z0-9-]+)\}$/;
const FENCE = /^```/;

/** Reads a page's title and sections from its Markdown; a heading without an id is an error. */
export function parseHelpPage(id: HelpPageId, markdown: string): HelpPage {
  const lines = markdown.replaceAll("\r\n", "\n").split("\n");
  const title = TITLE.exec(lines[0] ?? "")?.[1];
  if (title === undefined) throw new Error(`Help page ${id} starts without "# title".`);
  const lead: string[] = [];
  const sections: { id: string; heading: string; depth: 2 | 3; body: string[] }[] = [];
  let inFence = false;
  for (const line of lines.slice(1)) {
    if (FENCE.test(line)) inFence = !inFence;
    const heading = inFence ? null : HEADING.exec(line);
    if (heading !== null) {
      const [, marks = "", text = "", sectionId = ""] = heading;
      sections.push({ id: sectionId, heading: text, depth: marks.length === 2 ? 2 : 3, body: [] });
    } else if (!inFence && /^#{1,6} /.test(line)) {
      throw new Error(`Help page ${id}: the heading "${line}" needs a section id, "{#id}", or has the wrong level.`);
    } else {
      (sections.at(-1)?.body ?? lead).push(line);
    }
  }
  return {
    id,
    title,
    lead: lead.join("\n").trim(),
    sections: sections.map((section) => ({ ...section, body: section.body.join("\n").trim() })),
    markdown,
  };
}

const SOURCES = import.meta.glob<string>("./*/*.md", { query: "?raw", import: "default", eager: true });

/** The Markdown of one page in one language, or `null` if that file is missing. */
export function helpMarkdown(language: HelpLanguage, page: HelpPageId): string | null {
  return SOURCES[`./${language}/${page}.md`] ?? null;
}

const contentByLanguage = new Map<HelpLanguage, ReadonlyMap<HelpPageId, HelpPage>>();

/** Every page in `language`, parsed once; a page not yet translated shows in English. */
export function helpContent(language: HelpLanguage): ReadonlyMap<HelpPageId, HelpPage> {
  let pages = contentByLanguage.get(language);
  if (pages === undefined) {
    pages = new Map(HELP_PAGE_IDS.map((id) => {
      const markdown = helpMarkdown(language, id) ?? helpMarkdown("en", id);
      if (markdown === null) throw new Error(`Help page ${id} has no English text.`);
      return [id, parseHelpPage(id, markdown)] as const;
    }));
    contentByLanguage.set(language, pages);
  }
  return pages;
}

/** A required section reused outside the Help reader; missing shared content is an error. */
export function helpSection(language: HelpLanguage, page: HelpPageId, id: string): HelpSection {
  const section = helpContent(language).get(page)?.sections.find((candidate) => candidate.id === id);
  if (section === undefined) throw new Error(`Help page ${page} has no section ${id} in ${language}.`);
  return section;
}
